/* hs-eslint ignored failing-rules */

/* eslint-disable promise/catch-or-return */
'use es6';
/*
 * This loads theme CSS regardless of the `useDynamicCssLoading` prop.
 */

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import getComponentName from 'react-utils/getComponentName';
import { THEME_VALUES } from 'react-codemirror/util/Theme';
import Theme from 'react-codemirror/enum/Theme';
import { loadTheme } from 'react-codemirror/util/LoadCSS';
import { refresh } from 'react-codemirror/util/Editor';
const resolved = {};
const fetching = {};
/**
 * @param {Theme|String}  theme
 * @returns {Promise}
 */

function handleLoadTheme(theme) {
  if (resolved[theme]) return resolved[theme];
  if (fetching[theme]) return fetching[theme];
  const promise = new Promise(resolve => {
    loadTheme(theme).fetch().then(() => {
      resolve(theme);
      resolved[theme] = promise;
    }).catch(err => {
      console.warn('react-codemirror/decorator/themes()~loadTheme()', theme, err);
    }).finally(() => {
      fetching[theme] = null;
    });
  });
  fetching[theme] = promise;
  return promise;
}
/**
 * @typedef {Object} ThemesOptions
 * @property {Theme}                defaultTheme  - Optional.
 * @property {Theme|Array.<Theme>}  preload       - Optional. `useDynamicCssLoading=true` is assumed for preloads.
 */

/**
 * @param  {ThemesOptions} options
 * @return {Function}
 */


export default function themes(options) {
  options = options || {};
  const defaultTheme = options.defaultTheme || Theme.HUBSPOT_CANVAS_LIGHT;
  const preload = options.preload || []; // Download default and preset themes

  if (preload && preload.length) {
    const preloads = typeof preload === 'string' ? [preload] : preload;
    preloads.forEach(handleLoadTheme);
  }

  return function withThemes(CodemirrorComponent) {
    var _class, _temp;

    /**
     * @class
     */
    return _temp = _class = class WithThemes extends Component {
      constructor(props) {
        super(props);

        this.onThemeLoaded = theme => {
          if (theme !== this.themeFromProps) {
            // The theme from the async response does not match current props.
            return;
          }

          if (this.state.theme === theme) {
            // This would occur if async request was made in constructor or
            // by another editor instance.
            this.refresh();
            return;
          }

          this.setState({
            theme
          }, this.refresh);
        };

        this.refCodemirror = ref => {
          const codemirror = ref && ref.codemirror;
          if (this.codemirror === codemirror) return;
          this.codemirror = codemirror;

          if (this.props.refCodemirror) {
            this.props.refCodemirror(ref);
          }

          this.codemirrorDidMountOrUpdate(codemirror);
        };

        const _theme = this.themeFromProps;
        this.state = {
          theme: _theme
        }; // Start download asap

        this.fetchAndSetTheme();
      }

      componentDidUpdate(prevProps) {
        const {
          useDynamicCssLoading
        } = this.props;

        if (this.themeFromProps !== this.state.theme || useDynamicCssLoading !== prevProps.useDynamicCssLoading) {
          this.fetchAndSetTheme();
        }
      }

      codemirrorDidMountOrUpdate(codemirror) {
        if (!codemirror) return;
        this.fetchAndSetTheme();
      }

      get themeFromProps() {
        return this.props.theme || defaultTheme;
      }

      fetchAndSetTheme() {
        const {
          useDynamicCssLoading
        } = this.props;
        const theme = this.themeFromProps;

        if (useDynamicCssLoading && !resolved[theme]) {
          handleLoadTheme(theme).then(this.onThemeLoaded);
        } else if (theme !== this.state.theme) {
          this.setState({
            theme
          });
        }
      }

      refresh() {
        // This is debounced.
        refresh(this.codemirror);
      }

      render() {
        return /*#__PURE__*/_jsx(CodemirrorComponent, Object.assign({}, this.props, {
          refCodemirror: this.refCodemirror,
          theme: this.state.theme
        }));
      }

    }, _class.propTypes = {
      refCodemirror: PropTypes.func,

      /** @type {Theme} */
      theme: PropTypes.oneOf([...THEME_VALUES]),

      /**
       * TODO: Pull this in from loadCSS
       * @type {Boolean}
       */
      useDynamicCssLoading: PropTypes.bool
    }, _class.defaultProps = {
      theme: defaultTheme
    }, _class.displayName = `WithThemes(${getComponentName(CodemirrorComponent)})`, _temp;
  };
}